import React, { useState, useCallback, memo, useEffect } from 'react';
import {
  Mail,
  Phone,
  Calendar,
  Send,
  CheckCircle,
  AlertCircle,
  Github,
  Linkedin,
  MessageCircle,
  Clock
} from 'lucide-react';

// Mobile-first responsive input styles
const getInputStyles = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '14px' : '12px',
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  color: '#2C3E50',
  fontSize: isMobile ? '16px' : '14px', // 16px prevents zoom on iOS
  outline: 'none',
  fontFamily: 'inherit',
  transition: 'border-color 0.3s ease',
  minHeight: '48px', // Touch-friendly
  boxSizing: 'border-box',
  WebkitAppearance: 'none', // Remove iOS styling
  border: '2px solid rgba(150, 199, 185, 0.3)'
});

// Enhanced FormField with better mobile support
const FormField = memo(({
  label,
  name,
  type = 'text',
  placeholder,
  required = false,
  multiline = false,
  value,
  onChange
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const hasValue = value && value.length > 0;
  const borderStyle = hasValue
    ? '2px solid rgba(150, 199, 185, 0.6)'
    : '2px solid rgba(150, 199, 185, 0.3)';

  return (
    <div style={{
      marginBottom: isMobile ? '20px' : '24px',
      width: '100%'
    }}>
      <label style={{
        display: 'block',
        fontSize: isMobile ? '14px' : '13px',
        fontWeight: '500',
        color: '#546C81',
        marginBottom: '8px',
        lineHeight: '1.4'
      }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      
      <div style={{ position: 'relative' }}>
        {multiline ? (
          <textarea
            name={name}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            rows={isMobile ? 4 : 5}
            required={required}
            style={{
              ...getInputStyles(isMobile),
              resize: 'vertical',
              minHeight: '120px',
              maxHeight: '200px',
              border: borderStyle,
              lineHeight: '1.5'
            }}
          />
        ) : (
          <input
            name={name}
            type={type}
            value={value || ''}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            style={{
              ...getInputStyles(isMobile),
              border: borderStyle
            }}
          />
        )}
        
        <div style={{
          position: 'absolute',
          right: '12px',
          top: multiline ? '12px' : '50%',
          transform: multiline ? 'none' : 'translateY(-50%)',
          color: '#22c55e',
          opacity: hasValue ? 1 : 0,
          visibility: hasValue ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease, visibility 0.3s ease'
        }}>
          <CheckCircle size={18} />
        </div>
      </div>
    </div>
  );
});

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'nithinreddy3630@gmail.com',
      href: 'mailto:nithinreddy3630@gmail.com',
      description: 'Send me an email anytime'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 6305680684',
      href: 'tel:+916305680684',
      description: 'Call me Mon-Fri 9am-6pm IST'
    },
    {
      icon: Calendar,
      label: 'Schedule',
      value: 'Book a Call',
      href: 'https://calendly.com/nithinreddy3630/30min',
      description: 'Schedule a 30-min conversation'
    }
  ];

  const socialLinks = [
    {
      icon: Github,
      href: 'https://github.com/Nithinreddy000',
      label: 'GitHub'
    },
    {
      icon: Linkedin,
      href: 'https://www.linkedin.com/in/nithin-reddy1/',
      label: 'LinkedIn'
    }
  ];

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const subject = encodeURIComponent(formData.subject);
      const body = encodeURIComponent(
        `Name: ${formData.firstName} ${formData.lastName}\n` +
        `Email: ${formData.email}\n\n` +
        `Message:\n${formData.message}`
      );

      const mailtoLink = `mailto:nithinreddy3630@gmail.com?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;

      await new Promise(resolve => setTimeout(resolve, 1000));

      setFormStatus({
        type: 'success',
        message: 'Email client opened! Please send the email from your default email application.'
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      style={{
        padding: isMobile ? '40px 0' : '80px 0',
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
        position: 'relative'
      }}
    >
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '0 16px' : '0 32px',
        width: '100%'
      }}>

        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '40px' : '60px'
        }}>
          <p style={{
            color: '#96C7B9',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '16px'
          }}>
            Get In Touch
          </p>
          
          <h2 style={{
            fontFamily: 'Comfortaa, sans-serif',
            fontSize: isMobile ? '28px' : '48px',
            fontWeight: '600',
            color: '#2C3E50',
            marginBottom: '16px',
            lineHeight: '1.2'
          }}>
            Let's{' '}
            <span style={{
              background: 'linear-gradient(135deg, #96C7B9 0%, #7BB5A7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Connect
            </span>
          </h2>
          
          <p style={{
            fontSize: isMobile ? '16px' : '20px',
            color: '#546C81',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6',
            padding: isMobile ? '0 8px' : '0'
          }}>
            I'm always interested in hearing about new opportunities and exciting projects.
            Let's start a conversation!
          </p>
        </div>

        {/* Main Content */}
        <div style={{
          display: isMobile ? 'block' : 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '40px' : '48px',
          alignItems: 'start'
        }}>

          {/* Contact Info - Mobile First */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            order: isMobile ? 2 : 1
          }}>
            
            <div>
              <h3 style={{
                fontFamily: 'Comfortaa, sans-serif',
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '600',
                color: '#2C3E50',
                marginBottom: '24px'
              }}>
                Get in touch
              </h3>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <a
                      key={info.label}
                      href={info.href}
                      target={info.href.startsWith('http') ? '_blank' : undefined}
                      rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '12px' : '16px',
                        padding: isMobile ? '16px' : '20px',
                        backgroundColor: '#D1F0E0',
                        border: '2px solid rgba(150, 199, 185, 0.3)',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div style={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: 'rgba(150, 199, 185, 0.1)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Icon size={24} style={{ color: '#96C7B9' }} />
                      </div>
                      
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{
                          fontWeight: '500',
                          color: '#2C3E50',
                          marginBottom: '4px',
                          fontSize: isMobile ? '14px' : '16px'
                        }}>
                          {info.label}
                        </div>
                        <div style={{
                          color: '#96C7B9',
                          fontWeight: '500',
                          marginBottom: '4px',
                          fontSize: isMobile ? '13px' : '15px',
                          wordBreak: 'break-word'
                        }}>
                          {info.value}
                        </div>
                        <div style={{
                          fontSize: isMobile ? '12px' : '14px',
                          color: '#546C81',
                          lineHeight: '1.3'
                        }}>
                          {info.description}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 style={{
                fontFamily: 'Comfortaa, sans-serif',
                fontSize: isMobile ? '16px' : '20px',
                fontWeight: '600',
                color: '#2C3E50',
                marginBottom: '16px'
              }}>
                Follow me
              </h4>
              
              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '16px',
                        backgroundColor: '#D1F0E0',
                        border: '2px solid rgba(150, 199, 185, 0.3)',
                        borderRadius: '8px',
                        color: '#546C81',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icon size={20} />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Response */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(150, 199, 185, 0.1) 0%, rgba(150, 199, 185, 0.05) 100%)',
              border: '2px solid rgba(150, 199, 185, 0.2)',
              borderRadius: '12px',
              padding: isMobile ? '20px' : '24px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}>
                <Clock size={24} style={{ color: '#96C7B9' }} />
                <h4 style={{
                  fontFamily: 'Comfortaa, sans-serif',
                  fontSize: isMobile ? '16px' : '20px',
                  fontWeight: '600',
                  color: '#2C3E50',
                  margin: 0
                }}>
                  Quick Response
                </h4>
              </div>
              <p style={{
                color: '#546C81',
                fontSize: isMobile ? '14px' : '16px',
                lineHeight: '1.5',
                margin: 0
              }}>
                I typically respond to emails within 24 hours. For urgent matters,
                feel free to call or schedule a meeting directly.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div style={{
            order: isMobile ? 1 : 2
          }}>
            <div style={{
              backgroundColor: '#D1F0E0',
              borderRadius: '12px',
              padding: isMobile ? '24px' : '32px',
              border: '2px solid rgba(150, 199, 185, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <MessageCircle size={24} style={{ color: '#96C7B9' }} />
                <h3 style={{
                  fontFamily: 'Comfortaa, sans-serif',
                  fontSize: isMobile ? '18px' : '24px',
                  fontWeight: '600',
                  color: '#2C3E50',
                  margin: 0
                }}>
                  Send me a message
                </h3>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Name Fields */}
                <div style={{
                  display: isMobile ? 'block' : 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? '0' : '16px',
                  marginBottom: '0'
                }}>
                  <FormField
                    label="First Name"
                    name="firstName"
                    placeholder="First Name"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  <FormField
                    label="Last Name"
                    name="lastName"
                    placeholder="Last Name"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>

                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="your.email@example.com"
                  required
                  value={formData.email}
                  onChange={handleChange}
                />

                <FormField
                  label="Subject"
                  name="subject"
                  placeholder="What's this about?"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                />

                <FormField
                  label="Message"
                  name="message"
                  placeholder="Tell me about your project..."
                  required
                  multiline
                  value={formData.message}
                  onChange={handleChange}
                />

                {/* Form Status */}
                {formStatus.message && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: isMobile ? '16px' : '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    backgroundColor: formStatus.type === 'success'
                      ? 'rgba(34, 197, 94, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)',
                    border: formStatus.type === 'success'
                      ? '1px solid rgba(34, 197, 94, 0.2)'
                      : '1px solid rgba(239, 68, 68, 0.2)',
                    color: formStatus.type === 'success' ? '#22c55e' : '#ef4444'
                  }}>
                    <div style={{ flexShrink: 0, marginTop: '2px' }}>
                      {formStatus.type === 'success' ? (
                        <CheckCircle size={18} />
                      ) : (
                        <AlertCircle size={18} />
                      )}
                    </div>
                    <span style={{
                      fontSize: isMobile ? '14px' : '16px',
                      lineHeight: '1.4'
                    }}>
                      {formStatus.message}
                    </span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: isMobile ? '16px 24px' : '20px 32px',
                    background: isSubmitting
                      ? 'rgba(150, 199, 185, 0.5)'
                      : 'linear-gradient(135deg, #96C7B9 0%, #7BB5A7 100%)',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(150, 199, 185, 0.3)',
                    minHeight: '56px'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #FFFFFF',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Prevent zoom on input focus for iOS */
        @supports (-webkit-overflow-scrolling: touch) {
          input[type="email"],
          input[type="text"],
          textarea {
            font-size: 16px !important;
          }
        }

        /* Focus states */
        input:focus,
        textarea:focus {
          box-shadow: 0 0 0 3px rgba(150, 199, 185, 0.3) !important;
          outline: none !important;
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          button:hover,
          a:hover {
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Contact;
